import { FC, memo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddButton } from 'components/_basic/Form/FieldArray/components/AddButton';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';

import { concatWithDot } from 'helpers/string';

import { IForecastedConsumptionDataTableProps } from './types';
import {
  FORECASTED_CONSUMPTION_CHART_NAMES,
  REGION_PERIODS_NAMES,
  regionPeriodsInitialValues,
  YEARS_COLUMN_NAMES,
} from '../../../utils/form';
import { TableColumns } from '../../TableColumns';
import { TableRow } from '../../TableRow';

const columns = Object.values(YEARS_COLUMN_NAMES);

export const ForecastedConsumptionDataTable: FC<IForecastedConsumptionDataTableProps> = memo(
  ({ name, disabled, maxLength = 5 }) => {
    const { control, getValues } = useFormContext();
    const { t } = useTranslation();

    const columnsName = concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_HEADINGS);
    const rowsName = concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS);

    const { fields, append, remove } = useFieldArray({ control, name: rowsName });

    const canAddMore = fields.length < maxLength;

    const handleAddRow = () => {
      append(regionPeriodsInitialValues);
    };

    const addButtonTitle = getValues(concatWithDot(name, FORECASTED_CONSUMPTION_CHART_NAMES.REGION));

    return (
      <FieldTableContainer>
        <TableColumns
          name={columnsName}
          columnsNames={columns}
          firstColumnWidth={140}
          lastColumnWidth={36}
          inputMaxLength={50}
          disabled={disabled}
        />
        {fields.map((rowItem, index) => (
          <TableRow
            columnMultiline={false}
            canDeleteFirstRow
            key={rowItem.id}
            name={rowsName}
            rowIndex={index}
            columnsNames={columns}
            disabled={disabled}
            handleRemove={remove}
            firstColumnMaxLength={50}
            firstColumnWidth={140}
            rowName={concatWithDot(rowsName, index.toString() || '', REGION_PERIODS_NAMES.REGION)}
          />
        ))}
        <AddButton canAddMore={canAddMore} onClick={handleAddRow} disabled={disabled}>
          {t(`marketAnalysisForm.overview.forecastedConsumptionTable.${addButtonTitle}`)}
        </AddButton>
      </FieldTableContainer>
    );
  },
);
